import {useCallback, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {BreadcrumbsType} from "@/app/components/breadcrumbs/widget";
import {NearbyUsersModel, NewUserModel, UserRequests} from "@/app/api/user";
import {CourtBigModel, CourtsRequests} from "@/app/api/courts";
import {ReviewModel, ReviewsRequests, SetReviewRequestModel} from "@/app/api/reviews";
import {
  OpenMatchModelResponse,
  OpenMatchRemoveUserModel,
  OpenMatchRequestModel,
  OpenMatchStatusEnum,
  OpenMatchUpdateCourtModel,
  OpenMatchUpdateStatusModel
} from "@/app/api/open_match/models";
import {OpenMatchRequests} from "@/app/api/open_match/requests";
import {InvitationRequests, SendInvitationRequestModel} from "@/app/api/invitation";
import { logEventSafe } from "@/app/utils/analytics";
import { useSnackbar } from "@/app/utils/useSnackbar";
import { t } from "i18next";
import { s } from "@/app/utils/s";

interface ScreenState {
  hasError?: string;
  hasLoading: boolean;
  hasLoadingAddUser: boolean;
  hasLoadingRemoveUser: boolean;
  hasLoadingUpdateStatus: boolean;
  hasLoadingUpdateStatusCancel: boolean;
  hasLoadingLeaveMatch: boolean;
  hasLoadingReview: boolean;
  hasLoadingSendReview: boolean;
  items?: Array<BreadcrumbsType | string>;
  match?: OpenMatchModelResponse;
  location?: CourtBigModel;
  reviews?: ReviewModel[];
  users?: NearbyUsersModel[];
  owner?: NearbyUsersModel;
  otherPlayers?: NearbyUsersModel[];
  currentUser?: NewUserModel;
  isAdmin: boolean;
  showReview?: boolean;
  review: string;
  rating: number;
}

export const useMatchDetailPage = () => {
  const navigate = useNavigate();
  const locationRouter = useLocation();
  const {showMessage} = useSnackbar();
  const [state, setState] = useState<ScreenState>({
    hasLoading: true,
    hasLoadingAddUser: false,
    hasLoadingRemoveUser: false,
    hasLoadingUpdateStatus: false,
    hasLoadingUpdateStatusCancel: false,
    hasLoadingLeaveMatch: false,
    hasLoadingReview: false,
    hasLoadingSendReview: false,
    items: [BreadcrumbsType.padel, BreadcrumbsType.matchDetail],
    match: undefined,
    location: undefined,
    reviews: [],
    users: [],
    owner: undefined,
    otherPlayers: [],
    currentUser: undefined,
    isAdmin: false,
    showReview: false,
    review: '',
    rating: 0
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const loadData = useCallback(async (matchId: string, params: OpenMatchRequestModel) => {
    updateState({hasLoading: true});
    try {
      // Get matches with the provided parameters to narrow down the search
      const matches = await OpenMatchRequests.getMatches(params);

      // Find the specific match by ID
      const matchIdNum = parseInt(matchId);
      const match = matches.find(m => m.id === matchIdNum);

      if (!match) throw new Error("Match not found");

      // Get court details and reviews if available
      const courtId = match.court?.id;
      const location = courtId ? await CourtsRequests.getCourt({courtId}) : undefined;
      const reviews = courtId ? await ReviewsRequests.getCourtReview({courtId}) : [];

      // Get current user
      const currentUser = await UserRequests.getCurrentUser();

      // Determine if current user is admin (owner of the match)
      const isAdmin = match.owner?.id === currentUser.user.id;

      // Prepare users list (excluding owner)
      const users: NearbyUsersModel[] = [];
      const otherPlayers: NearbyUsersModel[] = [];

      // Add owner as a separate user
      const owner: NearbyUsersModel | undefined = match.owner ? {
        id: match.owner.id,
        username: match.owner.username || "",
        imageLink: match.owner.imageLink
      } : undefined;

      // Get details for other users if available
      if (match.users && match.users.length > 0) {
        // Here we would ideally fetch user details for each user ID
        // For now, we'll just create placeholder objects
        for (const u of match.users) {
          if (match.owner && u.id === match.owner.id) continue; // Skip owner

          // In a real implementation, you would fetch user details here
          // For now, we'll create a placeholder
          const user: NearbyUsersModel = {
            id: u.id,
            username: u.username, // Placeholder
            imageLink: u.imageLink
          };

          users.push(user);

          // Add to otherPlayers if not the current user
          if (u.id !== currentUser.user.id) {
            otherPlayers.push(user);
          }
        }
      }

      updateState({
        match,
        location,
        reviews,
        users,
        owner,
        otherPlayers,
        currentUser,
        isAdmin,
        hasError: undefined
      });
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoading: false});
    }
  }, [updateState]);

  useEffect(() => {
    const searchParams = new URLSearchParams(locationRouter.search);
    const matchId = searchParams.get("matchId");

    // Extract match parameters from query params
    const eventDateStr = searchParams.get("eventDate");

    // Create request model for getMatches
    // We need to modify the eventDate to be a Date object with only the date part (no time)
    // This will ensure it's serialized as YYYY-MM-DD when sent to the server
    let eventDate: Date | undefined = undefined;
    if (eventDateStr) {
      // Create a date object from the YYYY-MM-DD string
      // This ensures we don't have time components that cause LocalDate conversion issues
      const [year, month, day] = eventDateStr.split('-').map(Number);
      eventDate = new Date(year, month - 1, day); // month is 0-indexed in JS Date
    }

    const requestModel: OpenMatchRequestModel = {
      eventDate: eventDate
    };

    if (matchId) {
      loadData(matchId, requestModel);
    }
  }, [locationRouter.search, loadData]);

  // Add user to match via invitation
  const addUser = useCallback(async (userId: number) => {
    updateState({hasLoadingAddUser: true});
    try {
      if (!state.match?.id || !state.match?.court?.id) return;

      const model: SendInvitationRequestModel = {
        toUserIds: [userId],
        courtId: state.match.court.id,
        openMatchId: state.match.id
      };

      await InvitationRequests.sendInvitation(model);
      // Note: Invitation system doesn't return updated match, so we don't update the match state
      // The invitation will be handled through the invitation system
      updateState({hasError: undefined});
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoadingAddUser: false});
    }
  }, [state.match, updateState]);

  // Remove user from match
  const removeUser = useCallback(async (userId: number) => {
    updateState({hasLoadingRemoveUser: true});
    try {
      if (!state.match?.id) return;

      const model: OpenMatchRemoveUserModel = {
        matchId: state.match.id,
        userId: userId
      };

      const updatedMatch = await OpenMatchRequests.removeUser(model);
      updateState({match: updatedMatch, users: updatedMatch.users});
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoadingRemoveUser: false});
    }
  }, [state.match, updateState]);

  // Update match status
  const updateMatchStatus = useCallback(async (newStatus: OpenMatchStatusEnum) => {
    if(newStatus === OpenMatchStatusEnum.CANCELED) {
      updateState({hasLoadingUpdateStatusCancel: true});
    } else {
      updateState({hasLoadingUpdateStatus: true});
    }
    try {
      if (!state.match?.id) return;

      const model: OpenMatchUpdateStatusModel = {
        matchId: state.match.id,
        newStatus: newStatus
      };

      // If transitioning to STARTED, store start timestamp locally
      if (newStatus === OpenMatchStatusEnum.STARTED && state.match?.id) {
        try {
          localStorage.setItem(`matchStart:${state.match.id}`, Date.now().toString());
        } catch {}
      }

      const updatedMatch = await OpenMatchRequests.updateStatus(model);
      updateState({match: updatedMatch});

      // Log cancel by owner
      if (newStatus === OpenMatchStatusEnum.CANCELED && state.isAdmin && state.match?.id) {
        await logEventSafe('padel_match_cancel', { match_id: state.match.id });
        showMessage(t(s.matchCancelSuccess));
      }

      // Log complete with duration and players_count
      if (newStatus === OpenMatchStatusEnum.ARCHIVED && state.match?.id) {
        let duration_min: number | undefined = undefined;
        try {
          const key = `matchStart:${state.match.id}`;
          const startStr = localStorage.getItem(key);
          if (startStr) {
            const diffMs = Date.now() - parseInt(startStr, 10);
            duration_min = Math.max(0, Math.round(diffMs / 60000));
            localStorage.removeItem(key);
          }
        } catch {}

        await logEventSafe('padel_match_complete', {
          match_id: updatedMatch.id,
          club_id: updatedMatch.court?.id,
          duration_min,
          players_count: updatedMatch.users?.length
        });

        showMessage(t(s.matchEndSuccess));
      }
    } catch (error: any) {
      if (newStatus === OpenMatchStatusEnum.ARCHIVED) {
        showMessage(t(s.matchEndFailure), 4000, true);
      }
      if (newStatus === OpenMatchStatusEnum.CANCELED) {
        showMessage(t(s.matchCancelFailure), 4000, true);
      }
      updateState({hasError: error.message});
    } finally {
      if(newStatus === OpenMatchStatusEnum.CANCELED) {
        updateState({hasLoadingUpdateStatusCancel: false});
      } else {
        updateState({hasLoadingUpdateStatus: false});
      }
    }
  }, [showMessage, state.isAdmin, state.match, updateState]);

  // Leave match (for players)
  const leaveMatch = useCallback(async () => {
    updateState({hasLoadingLeaveMatch: true});
    try {
      if (!state.match?.id || !state.currentUser?.user.id) return;

      const model: OpenMatchRemoveUserModel = {
        matchId: state.match.id,
        userId: state.currentUser.user.id
      };

      const updatedMatch = await OpenMatchRequests.removeUser(model);
      updateState({match: updatedMatch});
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoadingLeaveMatch: false});
    }
  }, [state.match, state.currentUser, updateState]);

  // Toggle review bottom sheet
  const toggleShowReview = useCallback(() => {
    updateState({showReview: !state.showReview});
  }, [state.showReview, updateState]);

  // Set review text
  const onSetReview = useCallback((review: string) => {
    updateState({review});
  }, [updateState]);

  // Set rating
  const setRating = useCallback((rating: number) => {
    updateState({rating});
  }, [updateState]);

  // Send review
  const sendReview = useCallback(async () => {
    updateState({hasLoadingSendReview: true});
    if (!state.match?.id || !state.currentUser?.user.id || !state.location?.id) {
      toggleShowReview();
      updateState({hasLoadingSendReview: false});
      return;
    }

    try {
      // Add review for the court
      const reviewReq: SetReviewRequestModel = {
        rating: state.rating,
        comment: state.review
      };
      await ReviewsRequests.setCourtsReview(reviewReq, state.location.id);

      // Update match with the user's review ID
      const updateModel: OpenMatchUpdateCourtModel = {
        reviews: state.match.reviews?.includes(state.currentUser.user.id) ? state.match.reviews : [...state.match.reviews ?? [], state.currentUser.user.id]
      };
      await OpenMatchRequests.updateMatch(updateModel, state.match.id);

      // Reload match data to update UI
      if (state.match.id) {
        const matches = await OpenMatchRequests.getMatches({});
        const match = matches.find(m => m.id === state.match?.id);
        if (match) {
          updateState({match});
        }
      }

      toggleShowReview();
    } catch (error: any) {
      updateState({hasError: error.message});
    } finally {
      updateState({hasLoadingSendReview: false});
    }
  }, [state.match, state.currentUser, state.location, state.rating, state.review, updateState, toggleShowReview]);

  const userInMatch = useCallback((userId: number) => {
    return state.match?.users?.some(u => u.id === userId);
  }, [state.match]);

  return {
    state,
    addUser,
    removeUser,
    updateMatchStatus,
    leaveMatch,
    toggleShowReview,
    onSetReview,
    setRating,
    sendReview,
    userInMatch
  };
};